import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { X, AlertTriangle } from 'lucide-react'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import axios from 'axios'
import toast from '../../utilities/toast'

function BlockUserDialog({ isOpen, onClose, userId, userName }) {
  const [reason, setReason] = useState('')
  const [isBlocking, setIsBlocking] = useState(false)
  const [error, setError] = useState(null)

  const handleBlock = async () => {
    setIsBlocking(true)
    setError(null)

    try {
      await axios.post('/api/blocked_users', {
        blocked_user_id: userId,
        reason: reason.trim() || undefined
      })
      setReason('')
      toast.success(`${userName} has been blocked`)
      onClose(true)
    } catch {
      setError('Failed to block user. Please try again.')
      toast.error('Failed to block user')
    } finally {
      setIsBlocking(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Block User</h2>
          <Button onClick={() => onClose(false)} variant="ghost" size="icon">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4">
          <div className="flex items-start gap-3 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <p className="font-medium mb-1">Are you sure you want to block {userName}?</p>
              <p>They will no longer be able to send you messages, and you won&apos;t be able to message them.</p>
            </div>
          </div>

          <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you blocking this user?"
            className="min-h-[80px]"
            maxLength={500}
          />

          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <Button onClick={() => onClose(false)} variant="outline" disabled={isBlocking}>
            Cancel
          </Button>
          <Button onClick={handleBlock} disabled={isBlocking} className="bg-red-600 hover:bg-red-700">
            {isBlocking ? 'Blocking...' : 'Block User'}
          </Button>
        </div>
      </div>
    </div>
  )
}

BlockUserDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userName: PropTypes.string
}

export default BlockUserDialog
